/**
 * "Update the app" from the server: a full-screen stop, or a card she can close.
 *
 * Asks lib/app-update.ts at launch and every time the app comes back to the
 * foreground (one check at a time). Overlays rather than replaces the app,
 * like the app lock (components/app-lock/AppLockGate.tsx): a save or upload
 * already under way when the stop appears still finishes underneath, and
 * nothing in progress is unmounted. Mounted last in app/_layout.tsx, above
 * the app lock: there is no point unlocking an app that must be updated.
 *
 * A failed check changes nothing on screen (fails open): it neither shows a
 * stop nor lifts one already shown. A stop is lifted only by an answer that
 * says so, so turning the policy off in the Admin Hub releases every phone at
 * its next foreground.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
    AppState,
    Linking,
    Platform,
    StyleSheet,
    Text,
    TouchableOpacity,
    View
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";

import { getApiBase, loadApiEnv } from "@/lib/api-env";
import {
    currentAppLine,
    fetchAppUpdateDecision,
    NO_UPDATE,
    storeUrlFor,
    type AppUpdateDecision
} from "@/lib/app-update";
import { colors, fonts, radius, shadows, spacing } from "@/lib/theme";

// As said inside a sentence: "from the App Store", but "from Google Play".
const STORE_NAME = Platform.OS === "ios" ? "the App Store" : "Google Play";

export function AppUpdateGate() {
    const [decision, setDecision] = useState<AppUpdateDecision>(NO_UPDATE);
    // The target of a card she closed: it stays closed until the server names
    // a different version (or the app restarts).
    const [dismissedTarget, setDismissedTarget] = useState<string | null>(null);
    const checking = useRef(false);

    const check = useCallback(async () => {
        const appLine = currentAppLine();
        if (Platform.OS === "web" || !appLine || checking.current) {
            return;
        }
        checking.current = true;
        try {
            await loadApiEnv();
            const next = await fetchAppUpdateDecision({
                apiBase: getApiBase(),
                platform: Platform.OS,
                appLine
            });
            if (next) {
                setDecision(next);
            }
        } finally {
            checking.current = false;
        }
    }, []);

    useEffect(() => {
        if (Platform.OS === "web") {
            return;
        }
        void check();
        const sub = AppState.addEventListener("change", (state) => {
            if (state === "active") {
                void check();
            }
        });
        return () => sub.remove();
    }, [check]);

    const openStore = useCallback(() => {
        const url = storeUrlFor(Platform.OS);
        if (url) {
            void Linking.openURL(url);
        }
    }, []);

    if (decision.action === "require") {
        return (
            <View style={styles.cover} pointerEvents="auto">
                <SafeAreaView style={styles.safe}>
                    <View style={styles.wrap}>
                        <Text style={styles.title}>
                            {decision.title ?? "Update Task Health to continue"}
                        </Text>
                        <Text style={styles.body}>
                            {decision.body ??
                                `This version of the app is no longer supported. Install the new version from ${STORE_NAME} to keep working. Your account, visits and documents stay as they are.`}
                        </Text>
                        <TouchableOpacity
                            style={styles.button}
                            onPress={openStore}
                            accessibilityRole="button"
                        >
                            <Text style={styles.buttonText}>Update now</Text>
                        </TouchableOpacity>
                        <Text style={styles.help}>Need help? Text us at (929) 702-1946.</Text>
                    </View>
                </SafeAreaView>
            </View>
        );
    }

    if (decision.action === "recommend" && decision.targetVersion !== dismissedTarget) {
        return (
            <SafeAreaView style={styles.cardLayer} pointerEvents="box-none" edges={["top"]}>
                <View style={styles.card}>
                    <Text style={styles.cardTitle}>
                        {decision.title ?? "A new version of Task Health is ready"}
                    </Text>
                    <Text style={styles.cardBody}>
                        {decision.body ??
                            `Update from ${STORE_NAME} to get the latest fixes and improvements.`}
                    </Text>
                    <View style={styles.cardActions}>
                        <TouchableOpacity
                            style={styles.cardLater}
                            onPress={() => setDismissedTarget(decision.targetVersion)}
                            accessibilityRole="button"
                        >
                            <Text style={styles.cardLaterText}>Not now</Text>
                        </TouchableOpacity>
                        <TouchableOpacity
                            style={styles.cardUpdate}
                            onPress={openStore}
                            accessibilityRole="button"
                        >
                            <Text style={styles.cardUpdateText}>Update</Text>
                        </TouchableOpacity>
                    </View>
                </View>
            </SafeAreaView>
        );
    }

    return null;
}

const styles = StyleSheet.create({
    cover: {
        // absoluteFill, not absoluteFillObject: the same object on 4.7, and the
        // only one left on 4.8 (React Native 0.86 removed absoluteFillObject).
        ...StyleSheet.absoluteFill,
        // Above the app lock (9999) and everything under it.
        zIndex: 10000,
        elevation: 10000
    },
    safe: { flex: 1, backgroundColor: colors.gray50 },
    wrap: { flex: 1, justifyContent: "center", paddingHorizontal: 28, gap: spacing.base },
    title: { fontSize: 26, fontFamily: fonts.extraBold, color: colors.gray900 },
    body: { fontSize: 15, fontFamily: fonts.regular, color: colors.gray600, lineHeight: 22 },
    button: {
        marginTop: spacing.base,
        backgroundColor: colors.teal400,
        borderRadius: radius.lg,
        paddingVertical: 18,
        alignItems: "center"
    },
    buttonText: { fontSize: 16, fontFamily: fonts.bold, color: colors.ink },
    help: {
        fontSize: 13,
        fontFamily: fonts.regular,
        color: colors.gray600,
        textAlign: "center",
        marginTop: spacing.sm
    },
    cardLayer: {
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 10000,
        elevation: 10000
    },
    card: {
        marginHorizontal: spacing.base,
        marginTop: spacing.sm,
        padding: spacing.base,
        borderRadius: radius.lg,
        backgroundColor: colors.surface,
        borderWidth: 1,
        borderColor: colors.surfaceBorder,
        gap: spacing.sm,
        ...shadows.lg
    },
    cardTitle: { fontSize: 16, fontFamily: fonts.bold, color: colors.gray900 },
    cardBody: { fontSize: 14, fontFamily: fonts.regular, color: colors.gray600, lineHeight: 20 },
    cardActions: {
        flexDirection: "row",
        justifyContent: "flex-end",
        gap: spacing.sm,
        marginTop: spacing.xs
    },
    cardLater: {
        paddingVertical: 10,
        paddingHorizontal: spacing.base,
        borderRadius: radius.md
    },
    cardLaterText: { fontSize: 14, fontFamily: fonts.semiBold, color: colors.gray600 },
    cardUpdate: {
        paddingVertical: 10,
        paddingHorizontal: spacing.lg,
        borderRadius: radius.md,
        backgroundColor: colors.teal400
    },
    cardUpdateText: { fontSize: 14, fontFamily: fonts.bold, color: colors.ink }
});
